import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowUpRight, Filter, Plus, Scale, SlidersHorizontal, X } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { PermissionGate } from '@/components/common/PermissionGate'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
} from '@/components/common/DataTable'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useTaxRules, useDeactivateTaxRule } from '@/features/tax-rules/hooks/useTaxRules'
import type { TaxRule } from '@/features/tax-rules/types/tax-rule.types'
import { SupersededBadge } from './TaxRuleSuperseded'
import { isSuperseded } from '@/features/tax-rules/utils/taxRuleVersions'

const RULE_TYPE_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'success'> = {
  flat_rate: 'info',
  percentage: 'info',
  fixed_amount: 'warning',
  progressive_brackets: 'success',
  salary_range: 'success',
}

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
]

export function TaxRuleListPage(): React.ReactElement {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [deactivateId, setDeactivateId] = useState<string | null>(null)

  const page = Number(searchParams.get('page')) || 1
  const country = searchParams.get('country') || undefined
  const isActiveParam = searchParams.get('isActive')
  const isActive = isActiveParam === 'true' ? true : isActiveParam === 'false' ? false : undefined

  const { data, isLoading } = useTaxRules({ page, limit: 20, country, isActive })
  const deactivateMut = useDeactivateTaxRule()

  const rules = data?.data ?? []
  const total = data?.total ?? 0
  const limit = data?.limit ?? 20
  const hasFilters = Boolean(country || isActiveParam)

  const updateParam = (key: string, val: string): void => {
    const next = new URLSearchParams(searchParams)
    if (val) next.set(key, val)
    else next.delete(key)
    next.delete('page')
    setSearchParams(next)
  }

  const rateDisplay = (rule: TaxRule): string => {
    if (rule.ruleType === 'flat_rate' || rule.ruleType === 'percentage') {
      return rule.rate != null ? `${rule.rate.toFixed(2)}%` : '—'
    }
    if (rule.ruleType === 'fixed_amount') {
      return rule.fixedAmount != null ? rule.fixedAmount.toLocaleString() : '—'
    }
    if (rule.ruleType === 'progressive_brackets') {
      return `${rule.brackets.length} bracket(s)`
    }
    if (rule.ruleType === 'salary_range') {
      return `${rule.brackets.length} slab(s)`
    }
    return '—'
  }

  const clearFilters = (): void => {
    const next = new URLSearchParams(searchParams)
    next.delete('country')
    next.delete('isActive')
    next.delete('page')
    setSearchParams(next)
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Tax Rules' }]}
      icon={Scale}
      title="Tax Rules"
      description="Configure tax calculation rules and rates"
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <PermissionGate module="tax_rules" action="create">
              <Button size="sm" onClick={() => navigate('/tax-rules/new')}>
                <Plus className="h-3.5 w-3.5" />
                <span className="ml-1.5">New Tax Rule</span>
              </Button>
            </PermissionGate>
          }
        />
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col gap-5 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
                <SlidersHorizontal className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Rule library
                </p>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  {isLoading
                    ? 'Loading tax rules…'
                    : `${total} ${total === 1 ? 'rule' : 'rules'} in your tax configuration`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Active rules are applied to new payroll calculations
            </div>
          </div>

          <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/20 sm:px-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <Filter className="h-3.5 w-3.5" />
                    Country
                  </label>
                  <input
                    type="text"
                    placeholder="Any country"
                    maxLength={2}
                    className="h-11 w-36 rounded-xl border border-gray-200 bg-white px-3.5 text-sm uppercase text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] outline-none transition placeholder:normal-case placeholder:text-gray-400 focus:border-primary/40 focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    value={searchParams.get('country') ?? ''}
                    onChange={(e) => updateParam('country', e.target.value.toUpperCase())}
                  />
                </div>
                <Select
                  label="Status"
                  options={ACTIVE_OPTIONS}
                  value={searchParams.get('isActive') ?? ''}
                  onChange={(v) => updateParam('isActive', v)}
                  className="w-40"
                />
              </div>

              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="self-start md:self-auto"
                >
                  <X className="mr-1.5 h-3.5 w-3.5" />
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </section>

        {isLoading ? (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <TableSkeleton rows={5} cols={7} />
          </div>
        ) : rules.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/50 py-4 dark:border-gray-700 dark:bg-gray-800/50">
            <EmptyState
              icon={Scale}
              title={hasFilters ? 'No matching tax rules' : 'No tax rules configured yet'}
              description={
                hasFilters
                  ? 'Try adjusting or clearing the filters to see more rules.'
                  : 'Create your first rule to enable payroll tax calculations.'
              }
            />
          </div>
        ) : (
          <>
            <DataTable caption="Tax rules" className="rounded-2xl">
              <DataTableHead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <DataTableHeadCell className="pl-5 sm:pl-6">Tax rule</DataTableHeadCell>
                  <DataTableHeadCell>Country</DataTableHeadCell>
                  <DataTableHeadCell>Calculation</DataTableHeadCell>
                  <DataTableHeadCell>Rate / brackets</DataTableHeadCell>
                  <DataTableHeadCell>Effective date</DataTableHeadCell>
                  <DataTableHeadCell align="center">Version</DataTableHeadCell>
                  <DataTableHeadCell align="center">Status</DataTableHeadCell>
                  <DataTableHeadCell align="right" className="pr-5 sm:pr-6">
                    Actions
                  </DataTableHeadCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {rules.map((rule: TaxRule) => (
                  <DataTableRow
                    key={rule._id}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/tax-rules/${rule._id}`)}
                  >
                    <DataTableCell className="pl-5 py-3.5 sm:pl-6">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {rule.name}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Tax calculation rule
                      </div>
                    </DataTableCell>
                    <DataTableCell>
                      <span className="rounded-md bg-gray-100 px-2 py-1 font-mono text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {rule.country}
                      </span>
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant={RULE_TYPE_VARIANT[rule.ruleType] ?? 'neutral'}>
                        {rule.ruleType.replaceAll('_', ' ')}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell className="font-medium tabular-nums text-gray-700 dark:text-gray-200">
                      {rateDisplay(rule)}
                    </DataTableCell>
                    <DataTableCell className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(rule.effectiveFrom).toLocaleDateString()}
                    </DataTableCell>
                    <DataTableCell
                      align="center"
                      className="tabular-nums text-gray-600 dark:text-gray-300"
                    >
                      v{rule.version}
                      {isSuperseded(rule) && (
                        <div className="mt-1">
                          <SupersededBadge rule={rule} />
                        </div>
                      )}
                    </DataTableCell>
                    <DataTableCell align="center">
                      <Badge variant={rule.isActive ? 'success' : 'neutral'}>
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell align="right" className="pr-5 sm:pr-6">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGate module="tax_rules" action="delete">
                          {rule.isActive && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:text-danger dark:text-gray-400"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeactivateId(rule._id)
                              }}
                            >
                              Deactivate
                            </Button>
                          )}
                        </PermissionGate>
                        <ArrowUpRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
            {total > limit && (
              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={Math.ceil(total / limit)}
                onPageChange={(p) => {
                  const next = new URLSearchParams(searchParams)
                  next.set('page', String(p))
                  setSearchParams(next)
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Deactivate confirm */}
      <ConfirmDialog
        open={!!deactivateId}
        onClose={() => setDeactivateId(null)}
        onConfirm={() => {
          if (deactivateId) {
            deactivateMut.mutate(deactivateId, { onSuccess: () => setDeactivateId(null) })
          }
        }}
        title="Deactivate Tax Rule"
        message="This will deactivate the rule. Existing payroll runs using this rule are not affected."
        confirmLabel="Deactivate"
        variant="danger"
        confirmLoading={deactivateMut.isPending}
      />
    </PageShell>
  )
}

TaxRuleListPage.displayName = 'TaxRuleListPage'
